import dompurify from 'dompurify';
import { ExternalLink } from 'lucide-react';
import { Fragment, useCallback, useRef, useState } from 'react';
import scrollIntoView from 'scroll-into-view';
import { Button } from '@/components/ui/button';
import { useSyncScroll } from '@/hooks/useSyncScroll';
import { useTabLabels } from '@/hooks/useTabLabels';
import { StyledOrderedList } from '@/Shared/StyledElements';
import { StyledH5, StyledP, StyledSubHeader } from '@/Shared/Typography/typography';
import type { Details } from '@/stores/alignmentStore';
import { getFormattedDate, hasContent, sanitizeOptions } from '@/utils';

import FocusEntry from '../FocusEntry';

import { StyledCourseCard, StyledCourseCardHeader } from './CourseCard.Styles';

interface CourseCardProps {
	courseName: string;
	courseDivision: string;
	className: string;
	courseData: any;
	details: Details;
}

const OutcomeContent = ({ courseData, labels }: any) => {
	if (!courseData?.courses_outcomes) {
		return null;
	}
	const sanitizer = dompurify.sanitize;

	return (
		<div className="outcomes-block" data-section="outcomes">
			{courseData?.courses_outcomes?.length !== 0 && (
				<StyledH5 mb="16px">{labels.outcomes}:</StyledH5>
			)}
			<StyledOrderedList icon="bullseye">
				{courseData?.courses_outcomes
					.sort(
						(a: CoursesOutcomesEntity, b: CoursesOutcomesEntity) =>
							a.outcome.outcome_order - b.outcome.outcome_order
					)
					.map(({ outcome }: CoursesOutcomesEntity) => {
						const { outcome_id: id, outcome_text: text } = outcome;
						return (
							<li
								key={id}
								dangerouslySetInnerHTML={{
									__html: sanitizer(`${text}`, {
										...sanitizeOptions,
									}),
								}}
							/>
						);
					})}
			</StyledOrderedList>
		</div>
	);
};

const DescriptionContent = ({ courseData, labels }: any) => {
	const sanitizer = dompurify.sanitize;

	return (
		<div className="description-block" data-section="description">
			{hasContent(courseData?.course_description) && (
				<>
					<StyledH5 mb="16px">{labels.description}:</StyledH5>
					<StyledP
						dangerouslySetInnerHTML={{
							__html: sanitizer(`${courseData?.course_description}`, {
								...sanitizeOptions,
							}),
						}}
					/>
				</>
			)}
			{hasContent(courseData?.course_prereq) && (
				<>
					<StyledH5 mb="16px">{labels.prereq}:</StyledH5>
					<StyledP
						dangerouslySetInnerHTML={{
							__html: sanitizer(`${courseData?.course_prereq}`, {
								...sanitizeOptions,
							}),
						}}
					/>
				</>
			)}
			{hasContent(courseData?.course_extras) && (
				<>
					<StyledH5 mb="16px">{labels.extraInfo}:</StyledH5>
					<StyledP
						dangerouslySetInnerHTML={{
							__html: sanitizer(`${courseData?.course_extras}`, {
								...sanitizeOptions,
							}),
						}}
					/>
				</>
			)}
		</div>
	);
};

const ResourcesContent = ({ courseData, labels }: any) => {
	const sanitizer = dompurify.sanitize;
	const mediaResources = courseData?.courses_resources?.filter(
		({ resource }: CoursesResourcesEntity) => resource.resource_type === 'media'
	);
	const textResources = courseData?.courses_resources?.filter(
		({ resource }: CoursesResourcesEntity) => resource.resource_type === 'textbook'
	);
	const hasMediaResources = mediaResources?.length !== 0;
	const hasTextResources = textResources?.length !== 0;

	return (
		<div className="resources-block" data-section="resources">
			{hasContent(courseData?.course_resource_info) && (
				<>
					<StyledH5 mb="16px">{labels.resources}:</StyledH5>

					<StyledP
						dangerouslySetInnerHTML={{
							__html: sanitizer(`${courseData?.course_resource_info}`, {
								...sanitizeOptions,
							}),
						}}
					/>
				</>
			)}
			{hasMediaResources &&
				mediaResources?.map(({ resource }: CoursesResourcesEntity, idx: number) => {
					const {
						resource_id: id,
						resource_title: title,
						resource_author: author,
						resource_detail: detail,
					} = resource;
					return (
						<Fragment key={id}>
							{idx === 0 && <StyledH5 mb="16px">Media</StyledH5>}
							<div style={{ marginBottom: '24px' }}>
								{title && <StyledP mb="0">Title: {title}</StyledP>}
								{author && <StyledP mb="0">Author: {author}</StyledP>}
								{detail && (
									<StyledP mb="0">
										URL:{' '}
										<a className="media-link" href={detail} rel="noreferrer" target="_blank">
											Media Link
											<ExternalLink
												size={14}
												style={{ marginLeft: '4px' }}
												color="var(--org-color)"
											/>
										</a>
									</StyledP>
								)}
							</div>
						</Fragment>
					);
				})}
			{hasTextResources &&
				textResources?.map(({ resource }: CoursesResourcesEntity, idx: number) => {
					const {
						resource_id: id,
						resource_title: title,
						resource_isbn: isbn,
						resource_author: author,
						resource_detail: detail,
					} = resource;

					return (
						<Fragment key={id}>
							{idx === 0 && <StyledH5 mb="16px">Textbooks</StyledH5>}
							<div style={{ marginBottom: '24px' }}>
								{title && (
									<StyledP mb="0" fontSize="1.8rem">
										{title}
									</StyledP>
								)}
								{author && <StyledP mb="0">By: {author}</StyledP>}
								{detail && <StyledP mb="0">Description: {detail}</StyledP>}
								{isbn && <StyledP mb="0">ISBN: {isbn}</StyledP>}
							</div>
						</Fragment>
					);
				})}
		</div>
	);
};

const UnitsContent = ({ courseData, labels }: any) => {
	const sanitizer = dompurify.sanitize;

	return (
		<div className="units-focus-block" data-section="units">
			{courseData?.courses_focuses?.length !== 0 && <StyledH5 mb="16px">{labels.units}:</StyledH5>}
			<StyledOrderedList icon="brain">
				{courseData?.courses_focuses
					.sort(
						(a: CourseFocusEntity, b: CourseFocusEntity) =>
							a.focus.focus_order - b.focus.focus_order
					)
					.map(({ focus }: CourseFocusEntity) => {
						const { focus_id: id, focus_title: title, focuses_skills: skills } = focus;

						return (
							<Fragment key={id}>
								<li>
									<StyledP
										mb="16px"
										dangerouslySetInnerHTML={{
											__html: sanitizer(title, { ...sanitizeOptions }),
										}}
									/>
									<div className="sub-list">
										<FocusEntry type="question" options={skills} title={labels.question} />
										<FocusEntry type="topic" options={skills} title={labels.topic} />
										<FocusEntry type="skill" options={skills} title={labels.skill} />
									</div>
								</li>
							</Fragment>
						);
					})}
			</StyledOrderedList>
		</div>
	);
};

// Module-level flag prevents scroll event cascades across all card instances
const CourseCard = ({
	courseName,
	className,
	courseDivision,
	courseData,
	details,
}: CourseCardProps) => {
	const cardRef = useRef<any>(null);
	const [isOpen, setIsOpen] = useState(false);
	const labels = useTabLabels();

	function setClasses() {
		const base = `${className} ${courseData?.course_division}-card course-${courseData?.course_id}`;
		const openClass = isOpen ? 'open-card' : '';
		const syncClass = details.syncScroll ? 'sync-active' : '';
		return `${base} ${openClass} ${syncClass}`.trim();
	}

	const [maxHeight, setMaxHeight] = useState(400);
	const measuredRef = useCallback((node: any) => {
		if (node !== null) {
			cardRef.current = node;
			setMaxHeight(node.parentElement.clientHeight);
		}
	}, []);

	useSyncScroll(cardRef, details.syncScroll, isOpen);

	function toggleOpen(this: any) {
		setIsOpen(!isOpen);
		if (isOpen) {
			const scrollToCard = (cardElm: Element) => {
				scrollIntoView(cardElm as HTMLElement, {
					time: 0,
					align: {
						topOffset: 0,
					},
				});
			};
			scrollToCard(cardRef.current);
		}
	}

	const showContent = () => {
		if (!isOpen && details.showDescription) {
			return <DescriptionContent courseData={courseData} labels={labels} />;
		}
		if (!isOpen && details.showOutcomes) {
			return <OutcomeContent courseData={courseData} labels={labels} />;
		}
		if (!isOpen && details.showResources) {
			return <ResourcesContent courseData={courseData} labels={labels} />;
		}
		if (!isOpen && details.showUnits) {
			return <UnitsContent courseData={courseData} labels={labels} />;
		}

		return null;
	};

	return (
		<StyledCourseCard className={setClasses()} ref={measuredRef} $maxHeight={maxHeight}>
			<Button
				type="button"
				size="xs"
				onClick={() => toggleOpen()}
				className="
        sticky
				top-[calc(100%-40px)]
        bottom-2
        left-full
        z-10
        bg-[var(--org-color)]
        hover:bg-[var(--org-color-alt)]
        focus-visible:bg-[var(--org-color-alt)]
        hover:shadow-md
        focus-visible:shadow-md
        focus-visible:outline-none
        transition-all duration-300 ease-in-out
      "
			>
				{isOpen ? 'View Less' : 'View More'}
			</Button>
			<div className="content">
				<StyledCourseCardHeader data-section="header">
					<StyledH5 mb="0" className="card-course-name">
						{courseName}
					</StyledH5>
					<StyledSubHeader mb="0">{courseDivision}</StyledSubHeader>
					{courseData?.last_updated && (
						<time className="date" dateTime={courseData.last_updated}>
							Last Updated {getFormattedDate(courseData.last_updated)}
						</time>
					)}
				</StyledCourseCardHeader>
				{showContent()}
				{isOpen && (
					<>
						{details.showDescription && (
							<DescriptionContent courseData={courseData} labels={labels} />
						)}
						{details.showOutcomes && <OutcomeContent courseData={courseData} labels={labels} />}
						{details.showResources && <ResourcesContent courseData={courseData} labels={labels} />}
						{details.showUnits && <UnitsContent courseData={courseData} labels={labels} />}
					</>
				)}
			</div>
		</StyledCourseCard>
	);
};

export default CourseCard;
